import { patchModal, popModal, pushModal } from 'components/modal/store'
import { defineComponent, ref, watch } from 'vue'
import { useAddAsset } from '../../hooks/useAssetForm/hooks/useAddAsset'
import FormWizard from 'components/form-wizard'
import { sleep } from '@/utils/time'
import InitialStep from './initialStep'
import { computed } from 'vue'
import { useImportAssets } from '../../hooks/useAssetForm/hooks/useImportAssets'
import { clamp } from 'math'
import type { EAssetType } from 'findef'
import { FinsquidError } from '@/modules/providerImport/components/finsquid-error'

const AddAssetBody = defineComponent<{
  handleCancel: () => any
  handleSave: () => any
  onFinsquidComplete: () => any
  initialViewIndex?: number
}>(
  (props) => {

    const handleGoBack = (_index: number) => {
        queueMicrotask(() => {
          patchModal((s) => {
            s.hideTopBar = false
            return s
          })
          viewIndex.value = 0
        })
    }

    const addAsset = useAddAsset({
      onCancelOverride: props.handleCancel,
      redirectOnSave: false,
      onSave: props.handleSave,
      importAssetsModalOptions: {
        fullscreen: true
      },
      onLastGoBack: (index) => {
        handleGoBack(index)
      }
    })
    const importAssets = useImportAssets({
      onCancelOverride: props.handleCancel,
      onSave: props.handleSave,
      onLastGoBack: (index) => {
        handleGoBack(index)
      }
    })
    const viewIndex = ref<number>(props.initialViewIndex ?? 0)

    const handleClickConnectAccount = () => {
      viewIndex.value = 2
      patchModal((s) => {
        s.hideTopBar = true
        return s
      })
    }

    const handleSetAssetType = (assetType: EAssetType, listed: boolean = false) => {
      addAsset.setAssetType(assetType, listed)
      viewIndex.value = 1
      patchModal((s) => {
        s.hideTopBar = true
        return s
      })
    }

    watch(importAssets.errors, (newErrors, oldErrors) => {
      if (newErrors.length > 0) {
        patchModal((s) => {
          s.hideTopBar = false;
          return s;
        })
      } else if (newErrors.length === 0 && oldErrors.length > 0) {
        patchModal((s) => {
          s.hideTopBar = true;
          return s
        })
      }
    })

    const views = [
      () => (
        <div class="flex items-center py-8 lg:py-12">
          <InitialStep
            handleClickConnectAccount={handleClickConnectAccount}
            handleSetAssetType={handleSetAssetType}
          />
        </div>
      ),
      () => <FormWizard handler={addAsset.wizard} />,
      () => <FormWizard handler={importAssets.wizard} />
    ]

    const currentView = computed(() => views[clamp(viewIndex.value, 0, views.length - 1)])

    return () => {
      return (
        <div
          class="pretty-scrollbar"
          style={{
            width: '100%',
            height: '100%',
            maxWidth: '100vw',
            maxHeight: '100svh',
            overflow: 'auto'
          }}
        >
          {importAssets.errors.value.length > 0 ? (
            <FinsquidError importAssets={importAssets.providerImportHook}/>
          ) : currentView.value()}
        </div>
      )
    }
  },
  { props: ['handleCancel', 'handleSave', 'onFinsquidComplete', 'initialViewIndex'] }
)

export const pushAddAssetModal = async (
  props: {
    onSave?: () => any,
	initialViewIndex?: number
  } = {}
) => {

  const handleCancel = () => {
    popModal()
  }

  const handleFinsquidComplete = async () => {
    await sleep(300)
    await popModal() // pop finsquid modal
    await popModal() // pop this modal
    if (props.onSave) {
      await props.onSave()
    }
  }

  const handleSave = async () => {
    if (props.onSave) {
      await props.onSave()
    }
    popModal()
  }

  return await pushModal({
    fullscreen: true,
    hideActions: true,
    hideTopBar: (props.initialViewIndex ?? 0) > 0,
    disableDefaultActionStyles: true,
    disableDefaultContentStyles: true,
    title: 'Add Asset',
    body: () => {
      return (
        <AddAssetBody
          onFinsquidComplete={handleFinsquidComplete}
          handleCancel={handleCancel}
          handleSave={handleSave}
          initialViewIndex={props.initialViewIndex}
        />
      )
    }
  })
}
